import React, { useEffect, useState } from "react";
import { Button, Container, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useUser } from "../../../apis/user/UserProvider";
import { UsuarioPostVM } from "../../../services/userService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { useRole } from '../../../apis/role/RoleProvider';
import WarehouseSection from "../../../components/WarehouseSection";

const CreateUserView: React.FC = () => {
    const { t } = useTranslation();
    const { postUsuario } = useUser();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { roles, fetchRole } = useRole();
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    
    // Estado inicial vazio para um novo usuário
    const [formData, setFormData] = useState({
        id: 0,
        fullName: "",
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        enabled: false,
        accountExpired: false,
        accountLocked: false,
        passwordExpired: false,
        roleId: "",
        selectedWarehouses: [] as number[]
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                await fetchRole();
            } catch (err) {
                console.error("Erro ao carregar roles", err);
            }
        };

        fetchData();
    }, []);

    const navItems: NavBarItem[] = [
        { label: t('user.labels.home'), route: "/", iconName: "house-door" },
        { label: t('user.labels.list'), route: "/User", iconName: "list" },
        { label: t('user.labels.new'), route: "/User/create", iconName: "plus-circle" },
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = (): { isValid: boolean; errors: Record<string, string[]> } => {
        const errors: Record<string, string[]> = {};
        
        if (!formData.username.trim()) {
            errors.username = [getUiInterface("fieldRequiredSingle", { field: t("username") })];
        } else if (formData.username.length < 3 || formData.username.length > 50) {
            errors.username = [getUiInterface("minEMaxCaracters")];
        }
        
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = [getUiInterface("invalidEmail")];
        }

        if (!formData.email) {
            errors.email = [getUiInterface("fieldRequiredSingle", { field: t("email") })];
        }
        
        if (formData.fullName && formData.fullName.length > 100) {
            errors.fullName = [getUiInterface("maxCaracters")];
        }
        
        if (!formData.password) {
            errors.password = [getUiInterface("fieldRequiredSingle", { field: t("password") })];
        }
        if (!formData.confirmPassword) {
            errors.confirmPassword = [getUiInterface("fieldRequiredSingle", { field: t("confirmPassword") })];
        } else if (formData.password.length < 6 || formData.password.length > 100) {
            errors.password = [getUiInterface("passwordMinimumLength")];
        }
        
        if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = [getUiInterface("noEqualsPassword")];
        }
        
        if (!formData.roleId) {
            errors.roleId = [getUiInterface("selectRole")];
        }
        
        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(false);
        setErrorMessage(null);
        
        const { isValid, errors } = validateForm();
        
        if (!isValid) {
            setValidationErrors(errors);
            setError(true);
            setLoading(false);
            return;
        }

        try {
            const payload: UsuarioPostVM = {
                id: formData.id,
                fullName: formData.fullName,
                userName: formData.username,
                email: formData.email,
                password: formData.password,
                enabled: formData.enabled,
                accountExpired: formData.accountExpired,
                accountLocked: formData.accountLocked,
                passwordExpired: formData.passwordExpired,
                confirmPassword: formData.confirmPassword,
                roleIds: [Number(formData.roleId)],
                wareHouseClientIds: formData.selectedWarehouses
            };

            console.log('Enviando payload:', payload);
            const response = await postUsuario(payload);
            console.log('Resposta recebida:', response);
            
            if (response) {
                // Tenta extrair o ID de diferentes formatos de resposta
                const userId = response.data?.id || response.data?.id;
                console.log('UserID extraído:', userId);
                
                if (userId) {
                    const successMessage = getSuccessMessage("createUser", { id: userId });
                    console.log('Navegando para /User com mensagem de sucesso');
                    navigate(`/User`, {
                        state: {
                            message: successMessage,
                            variant: "success",
                        },
                    });
                    return;
                }
            }

            console.warn('Resposta sem ID válido:', response);
            throw new Error(getUiInterface("createUserError"));
        } catch (error: any) {
            console.error("Erro ao criar usuário:", error);
            setError(true);
            
            if (error.response?.data?.errors) {
                setValidationErrors(error.response.data.errors);
            } else if (error.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage(error.message || getUiInterface("createUserError"));
            }
        } finally {
            setLoading(false);
        }
    };

    const renderFieldErrors = (fieldName: string) => {
        if (!validationErrors[fieldName]) return null;
        
        return (
            <div className="text-danger mt-1">
                {validationErrors[fieldName].map((error, index) => (
                    <div key={index}>{error}</div>
                ))}
            </div>
        );
    };
    const customStyles = {
        errorBorder: {
            borderColor: '#dc3545',
            backgroundImage: 'none', 
            paddingRight: 'calc(1.5em + 0.75rem)', 
            '&:focus': {
                borderColor: '#dc3545',
                boxShadow: '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
            }
        }
    };
    return (
        <Container fluid>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("createUser")}</h2>
            
            {errorMessage && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}
            
            <Form onSubmit={handleSubmit}>
                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.name')}</Form.Label>
                        <Form.Control
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                        />
                         {renderFieldErrors("fullName")}
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.username')} *</Form.Label>
                        <Form.Control
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.username}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {renderFieldErrors("username")}
                    </Form.Group>
                </div>

                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.password')} *</Form.Label>
                        <Form.Control
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.password}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {renderFieldErrors("password")}
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.confirmPassword')} *</Form.Label>
                        <Form.Control
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.confirmPassword}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {renderFieldErrors("confirmPassword")}
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.email')} *</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.email}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {renderFieldErrors("email")}
                    </Form.Group>
                </div>

                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ width: "30%" }}>
                        <Form.Label>{t('user.fields.role')} *</Form.Label>
                        <Form.Select
                            name="roleId"
                            value={formData.roleId}
                            onChange={handleSelectChange}
                            isInvalid={!!validationErrors.roleId}
                            style={validationErrors.roleId ? {
                                borderColor: '#dc3545',
                                backgroundImage: 'none'
                            } : undefined}
                        >
                            <option value="">{t('user.labels.selectRole')}</option>
                            {roles?.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.authority}
                                </option>
                            ))}
                        </Form.Select>
                         {renderFieldErrors("roleId")}
                    </Form.Group>

                    <Form.Group className="d-flex align-items-center gap-4">
                        <Form.Check
                            type="checkbox"
                            name="enabled"
                            label={t('user.fields.enabled')}
                            checked={formData.enabled}
                            onChange={handleInputChange}
                        />
                        <Form.Check
                            type="checkbox"
                            name="accountExpired"
                            label={t('user.fields.accountExpired')}
                            checked={formData.accountExpired}
                            onChange={handleInputChange}
                        />
                        <Form.Check
                            type="checkbox"
                            name="accountLocked"
                            label={t('user.fields.accountLocked')}
                            checked={formData.accountLocked}
                            onChange={handleInputChange}
                        />
                        <Form.Check
                            type="checkbox"
                            name="passwordExpired"
                            label={t('user.fields.passwordExpired')}
                            checked={formData.passwordExpired}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                </div>

                <WarehouseSection
                    selectedWarehouses={formData.selectedWarehouses}
                    setSelectedWarehouses={(warehouses) => 
                        setFormData(prev => ({ ...prev, selectedWarehouses: warehouses }))
                    }
                    isEditing={true}
                />

                <div className="mt-3 mb-3 d-flex gap-2">
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? t('user.labels.saving') : getUiInterface("save")}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/User')}>
                        {getUiInterface("cancel")}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default CreateUserView;